import { Box, styled, Typography } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { StoreInformationChip } from 'src/components/molecules/StoreInformationChip/StoreInformationChip';
import { useFilterContext } from 'src/contexts/FilterContext';
import { Filter } from 'src/domain';
import { ChainMap, StoreGroup, StoreSelectionList } from 'src/domain/models/store/StoreSelection.types';
import { StoreType } from 'src/infrastructure/rest-api/api-types';
import { buildPathFromContext } from 'src/utils/buildPath';

const MeasureChainWrapper: FC<{
    chainId: string;
    chain: ChainMap;
    top: number;
    setMeasurement: (id: string, height: number) => void;
    storeType: StoreType;
    filterContext: Filter;
    width: string;
}> = ({ chainId, chain, top, setMeasurement, storeType, filterContext, width }) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (ref.current) {
            setMeasurement(chainId, ref.current.offsetHeight);
        }
    }, [chainId, setMeasurement]);

    return (
        <Box
            ref={ref}
            sx={{
                position: 'absolute',
                top,
                width: '100%',
                paddingBottom: 2,
                boxSizing: 'border-box',
            }}
        >
            <ChainWrapper chain={chain.chainName} width={width}>
                <ChainTitle>{chain.chainName}</ChainTitle>
                <CountryStoresList>
                    {Array.from(chain.groups.entries()).map(([_, group], _index, groupArray) => (
                        <CountryWrapper key={group.groupId} length={groupArray.length}>
                            <StyledLink
                                to={{
                                    pathname: '/turnover',
                                    search: buildPathFromContext(
                                        chainId.toString(),
                                        storeType,
                                        filterContext.grouping,
                                        'overview',
                                        group.groupKey
                                    ),
                                }}
                            >
                                <CountryTitle>{group.groupName}</CountryTitle>
                            </StyledLink>
                            {group.stores.map((store) => (
                                <StyledLink
                                    key={`store-${store.id}`}
                                    to={{
                                        pathname: '/turnover',
                                        search: buildPathFromContext(
                                            chainId.toString(),
                                            storeType,
                                            filterContext.grouping,
                                            store.id.toString() ?? '',
                                            group.groupKey
                                        ),
                                    }}
                                >
                                    <ItemLabel>
                                        <span style={{ marginTop: 'auto', marginBottom: 'auto' }}>
                                            {store.storeName}
                                        </span>
                                        <StoreInformationChip store={store} />
                                    </ItemLabel>
                                </StyledLink>
                            ))}
                        </CountryWrapper>
                    ))}
                </CountryStoresList>
            </ChainWrapper>
        </Box>
    );
};

interface Props {
    stores: StoreSelectionList;
    storeType: StoreType;
}

const HEADER_AND_CONTROLS_HEIGHT = 250;

const SelectionList: FC<Props> = ({ stores, storeType }) => {
    const [filterContext] = useFilterContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const brandList = useMemo(() => Array.from(stores.entries()), [stores]);

    const [heightMeasurements, setHeightMeasurements] = useState<Map<string, number>>(new Map());

    const setMeasurement = (chainId: string, height: number) => {
        setHeightMeasurements((prev) => {
            if (prev.get(chainId) === height) return prev;
            const newMap = new Map(prev);
            newMap.set(chainId, height);
            return newMap;
        });
    };

    const rowVirtualizer = useVirtualizer({
        count: brandList.length,
        getScrollElement: () => containerRef.current,
        estimateSize: (index) => {
            const [chainId] = brandList[index];
            return heightMeasurements.get(chainId) ?? 450;
        },
        getItemKey: (index) => brandList[index][0],
    });

    const chainWidths = useMemo(() => {
        const widths = new Map<string, string>();

        Array.from(stores.entries()).forEach(([chainId, chain]) => {
            const nbrOfGroupings = Array.from(chain.groups.entries()).length;
            const nbrOfStores = Array.from<[string, StoreGroup]>(stores.get(chainId)?.groups.entries() ?? []).reduce(
                (acc, [_, { stores }]) => acc + stores.length,
                0
            );

            const height = Math.floor((nbrOfStores * 15) / 120);
            let width: string;

            if (nbrOfGroupings >= 8) {
                width = '100%';
            } else {
                width = height > 0 ? `${(height + 3) * 160}px` : `${nbrOfGroupings * 160}px`;
            }

            widths.set(chainId, width);
        });

        return widths;
    }, [stores]);

    return (
        <Box
            ref={containerRef}
            sx={{
                height: Math.min(globalThis.innerHeight - HEADER_AND_CONTROLS_HEIGHT, rowVirtualizer.getTotalSize()),
                overflowY: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
            }}
        >
            <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const [chainId, chain] = brandList[virtualRow.index];
                    return (
                        <MeasureChainWrapper
                            key={chainId}
                            chainId={chainId}
                            chain={chain}
                            top={virtualRow.start}
                            setMeasurement={setMeasurement}
                            storeType={storeType}
                            filterContext={filterContext}
                            width={chainWidths.get(chainId) || '100%'}
                        />
                    );
                })}
            </div>
        </Box>
    );
};

const CountryStoresList = styled(Box)`
    column-count: auto;
    column-width: 200px;
    position: relative;
    border: 1px dashed ${({ theme }) => theme.palette.grey[800]};
    border-radius: 2px;
    padding: 15px 10px;
    flex: 1;
`;

const ChainWrapper = styled(Box)<{
    width: string;
    chain: string;
}>`
  display: flex;
  position: relative;
  ${(props) =>
      props.chain.toLowerCase() === 'object collectors item' &&
      `
  min-width: 250px;
  `}
  width: ${(props) => `min(${props.width}, 100%)`};
  margin-top: 16px
`;

const ChainTitle = styled(Typography)(({ theme }) => ({
    fontSize: '16px',
    fontWeight: 500,
    position: 'absolute',
    zIndex: 1,
    top: '-10px',
    left: '5px',
    padding: '0px 5px',
    backgroundColor: theme.palette.common.white,
    alignSelf: 'flex-start',
    textTransform: 'uppercase',
}));

const CountryWrapper = styled(Box)<{ length: number }>`
  margin-bottom: ${(props) => (props.length > 1 ? `10px` : `0px`)};
`;

const CountryTitle = styled(Typography)`
  font-size: 12px;
  margin: 0;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const ItemLabel = styled(Typography)`
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin: 0;
  font-size: 12px;
  line-height: normal;
  white-space: pre-wrap;
  max-width: 200px;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.text.primary,
}));

export default SelectionList;
